import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UploadSection, 
  ConfigurationPanel, 
  ResultsSection, 
  JobProgress,
  LoadingOverlay
} from './components';
import { AnalysisResult, AsyncJobResponse } from './types';
import { MeetingType } from './types/sessionContext';
import { AnalysisService, SessionService } from './services';
import { SessionContextService } from './services/sessionContextService';
import { SALES_STAGES, MESSAGES } from './constants';

// Type guard function
const isAsyncJobResponse = (data: any): data is AsyncJobResponse => {
  return data && typeof data === 'object' && data.type === 'async_job';
};

export const MainApp: React.FC = () => {
  const navigate = useNavigate();
  const [sessionId] = useState<string>(() => SessionService.getSessionId()); // Get persistent session ID
  const [transcript, setTranscript] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');
  const [numSpeakers, setNumSpeakers] = useState<number>(2);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [salesStage, setSalesStage] = useState<string>('');
  const [results, setResults] = useState<AnalysisResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [contextLoading, setContextLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [liveRecordingFile, setLiveRecordingFile] = useState<string | null>(null);
  const [liveRecordingUrl, setLiveRecordingUrl] = useState<string | null>(null);
  const [uploadedAudioFile, setUploadedAudioFile] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);

  useEffect(() => {
    const loadMeetingTypes = async () => {
      try {
        setContextLoading(true);
        const sessionContext = await SessionContextService.getSessionContext(sessionId);
        
        if (sessionContext && sessionContext.meetingTypes && Array.isArray(sessionContext.meetingTypes) && sessionContext.meetingTypes.length > 0) {
          setMeetingTypes(sessionContext.meetingTypes);
          setSalesStage(sessionContext.meetingTypes[0].name);
        } else {
          // Fallback to default meeting types
          const defaultMeetingTypes = await SessionContextService.getDefaultMeetingTypes();
          if (Array.isArray(defaultMeetingTypes) && defaultMeetingTypes.length > 0) {
            setMeetingTypes(defaultMeetingTypes);
            setSalesStage(defaultMeetingTypes[0].name);
          } else {
            // Final fallback to static SALES_STAGES
            const fallbackTypes: MeetingType[] = SALES_STAGES.map((stage, index) => ({
              id: `fallback-${index}`,
              name: stage,
              description: undefined
            }));
            setMeetingTypes(fallbackTypes);
            setSalesStage(SALES_STAGES[0]);
          }
        }
      } catch (err) {
        // If session context service fails, use fallback
        const fallbackTypes: MeetingType[] = SALES_STAGES.map((stage, index) => ({
          id: `fallback-${index}`,
          name: stage,
          description: undefined
        }));
        setMeetingTypes(fallbackTypes);
        setSalesStage(SALES_STAGES[0]);
      } finally {
        setContextLoading(false);
      }
    };

    loadMeetingTypes();
  }, [sessionId]);

  const handleAnalyze = async () => {
    console.log('🔥 handleAnalyze called - uploadedAudioFile:', uploadedAudioFile);
    setLoading(true);
    setError('');

    try {
      let data;
      if (uploadedAudioFile) {
        // uploadedAudioFile contains the public URL, use it as audioUrl
        console.log('🔥 Using uploadedAudioFile for analysis:', uploadedAudioFile);
        data = await AnalysisService.analyzeTranscript('', null, salesStage, uploadedAudioFile, numSpeakers, sessionId);
      } else if (transcript.trim() || file || audioUrl.trim()) {
        data = await AnalysisService.analyzeTranscript(transcript, file, salesStage, audioUrl, numSpeakers, sessionId);
      } else {
        setError(MESSAGES.ERRORS.NO_TRANSCRIPT);
        setLoading(false);
        return;
      }

      if (isAsyncJobResponse(data)) {
        setCurrentJobId(data.jobId);
      } else {
        const analysisResult = data as AnalysisResult;
        setResults(analysisResult);
        if (analysisResult.analysisId) {
          navigate(`/analysis/${analysisResult.analysisId}`, {
            replace: true,
            state: { results: analysisResult },
          });
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : MESSAGES.ERRORS.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTranscript('');
    setFile(null);
    setAudioUrl('');
    setNumSpeakers(2);
    setResults(null);
    setError('');
    setCurrentJobId(null);
    setLiveRecordingFile(null);
    setLiveRecordingUrl(null);
    setUploadedAudioFile(null);
    setSelectedPreset(null);
    setSalesStage(meetingTypes[0]?.name || SALES_STAGES[0]);
  };

  const handleJobComplete = (result: AnalysisResult) => {
    setResults(result);
    setCurrentJobId(null);
    
    // Navigate to permalink if analysis has an ID
    if (result.analysisId) {
      navigate(`/analysis/${result.analysisId}`, { 
        replace: true, 
        state: { results: result } 
      });
    }
  };

  const handleJobCancel = () => {
    setCurrentJobId(null);
    setError('');
  };

  const handleRecordingReady = (filename: string, url: string) => {
    // Treat live recording the same as uploaded audio file
    setUploadedAudioFile(url);
    // Clear other inputs
    setTranscript('');
    setFile(null);
    setAudioUrl('');
    setLiveRecordingFile(null);
    setLiveRecordingUrl(null);
  };

  const handleClearUploadedAudio = () => {
    setUploadedAudioFile(null);
    setFile(null); // Also clear file state if needed
    setLiveRecordingFile(null);
    setLiveRecordingUrl(null);
  };

  const handleAudioFileUpload = async (audioFile: File) => {
    setLoading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('audio', audioFile);
      formData.append('salesStage', salesStage);

      const response = await fetch('/api/audio/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      const result = await response.json();

      // Store the public URL for Fireflies
      console.log('🔥 Setting uploadedAudioFile to:', result.fileInfo.publicUrl);
      setUploadedAudioFile(result.fileInfo.publicUrl);

      // Clear other inputs when audio file is uploaded
      setTranscript('');
      setFile(null);
      setAudioUrl('');
      setLiveRecordingFile(null);
      setLiveRecordingUrl(null);
      setSelectedPreset(null);

    } catch (error) {
      console.error('Error uploading audio file:', error);
      setError('Nie udało się przesłać pliku audio. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {currentJobId ? (
          <div className="max-w-4xl mx-auto">
            <JobProgress
              jobId={currentJobId}
              onComplete={handleJobComplete}
              onCancel={handleJobCancel}
            />
          </div>
        ) : !results ? (
          <div className="max-w-4xl mx-auto space-y-8">
            <UploadSection
              transcript={transcript}
              onTranscriptChange={setTranscript}
              file={file}
              onFileChange={setFile}
              audioUrl={audioUrl}
              onAudioUrlChange={setAudioUrl}
              onAsyncJobStart={setCurrentJobId}
              numSpeakers={numSpeakers}
              onNumSpeakersChange={setNumSpeakers}
              error={error}
              sessionId={sessionId}
              salesStage={salesStage}
              onRecordingReady={handleRecordingReady}
              selectedPreset={selectedPreset}
              onPresetSelect={setSelectedPreset}
              onAudioFileUpload={handleAudioFileUpload}
              uploadedAudioFile={uploadedAudioFile}
              onClearUploadedAudio={handleClearUploadedAudio}
            />
            
            {contextLoading ? (
              <div className="glass-card rounded-3xl p-8 backdrop-blur-xl animate-pulse">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-center mt-4 text-gray-600 dark:text-gray-300">
                  Ładowanie konfiguracji...
                </p>
              </div>
            ) : (
              <ConfigurationPanel
                salesStage={salesStage}
                onSalesStageChange={setSalesStage}
                onAnalyze={handleAnalyze}
                meetingTypes={meetingTypes}
                disabled={loading || (!transcript.trim() && !file && !audioUrl.trim() && !uploadedAudioFile)}
              />
            )}
          </div>
        ) : (
          <ResultsSection
            results={results}
            onReset={handleReset}
          />
        )}
      
      {loading && <LoadingOverlay />}
    </>
  );
};